import {Suspense, useMemo} from 'react';
import {BrowserRouter} from 'react-router-dom';
import {QueryClientProvider} from '@tanstack/react-query';
import {ReactQueryDevtools} from '@tanstack/react-query-devtools';
import {App as AntApp, ConfigProvider, Spin} from 'antd';
import ruRU from 'antd/locale/ru_RU';
import {useAtomValue} from 'jotai';

import {queryClient} from '@queries/queryClient';
import {accentChoiceAtom, antdBrandOverrideFor} from '@atoms/accent';
import {effectiveThemeAtom} from '@atoms/theme';
import {ThemeModeProvider} from '@components/ThemeModeProvider';
import {AppRouter} from '@routes/AppRouter';
import {SessionGate} from '@routes/SessionGate';
import {buildAntdTheme} from '@styles/antd-theme';

/* Пока грузится чанк страницы. Спиннер по центру области контента —
   шапка и меню уже на месте, перерисовывать их незачем. */
const RouteFallback = () => (
  <div style={{display: 'flex', justifyContent: 'center', padding: '48px 0'}}>
    <Spin />
  </div>
);

// Внутренний компонент: читает атомы темы/акцента и собирает AntD ThemeConfig.
// <AntApp> нужен чтобы статический message.* / notification.* умел
// потреблять theme-context и реально рендерил тосты.
const ThemedRouter = () => {
  const mode = useAtomValue(effectiveThemeAtom);
  const accent = useAtomValue(accentChoiceAtom);

  const theme = useMemo(
    () => buildAntdTheme(mode, antdBrandOverrideFor(accent, mode)),
    [mode, accent],
  );

  return (
    <ConfigProvider theme={theme} locale={ruRU}>
      <AntApp>
        {/* Future-флаги v7 включены заранее: они убирают предупреждения
            в консоли и, что важнее, дают то поведение, к которому мы
            всё равно придём при обновлении роутера.

            v7_startTransition — переходы оборачиваются в
            startTransition. С ленивыми страницами это ровно то, что
            нужно: React держит на экране прошлую страницу, пока
            грузится чанк следующей, вместо мгновенного спиннера.

            v7_relativeSplatPath — относительные ссылки внутри splat-
            маршрутов (`*`) считаются от самого маршрута, а не от его
            родителя. У нас splat один — 404, — так что менять
            поведение нечему, но пусть будет явно. */}
        <BrowserRouter future={{v7_startTransition: true, v7_relativeSplatPath: true}}>
          {/* Сохранённую сессию проверяем до отрисовки приложения —
              иначе с протухшим токеном админка успевает мелькнуть. */}
          <SessionGate>
            {/* Страницы грузятся лениво — один общий фолбэк на все
                переходы. Отдельный на каждый роут дал бы мельтешение
                разных заглушек. */}
            <Suspense fallback={<RouteFallback />}>
              <AppRouter />
            </Suspense>
          </SessionGate>
        </BrowserRouter>
      </AntApp>
    </ConfigProvider>
  );
};

export const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeModeProvider>
      <ThemedRouter />
    </ThemeModeProvider>
    {import.meta.env.DEV && <ReactQueryDevtools buttonPosition="bottom-right" />}
  </QueryClientProvider>
);
